import { toast as sonnerToast } from 'sonner';

// Counter for generating unique IDs
let toastCounter = 0;

/**
 * Generate a clean, safe toast ID
 * @param {string} prefix - Optional prefix for the ID
 * @returns {string} Clean toast ID
 */
const generateToastId = (prefix = 'toast') => {
  toastCounter++;
  return `${prefix}-${Date.now()}-${toastCounter}`;
};

/**
 * Enhanced toast wrapper with automatic safe ID generation
 */
const toast = {
  success: (message, options = {}) => {
    const id = options.id || generateToastId('success');
    return sonnerToast.success(message, { ...options, id });
  },

  error: (message, options = {}) => {
    const id = options.id || generateToastId('error');
    return sonnerToast.error(message, { ...options, id });
  },

  info: (message, options = {}) => {
    const id = options.id || generateToastId('info');
    return sonnerToast.info(message, { ...options, id });
  },

  warning: (message, options = {}) => {
    const id = options.id || generateToastId('warning');
    return sonnerToast.warning(message, { ...options, id });
  },

  loading: (message, options = {}) => {
    const id = options.id || generateToastId('loading');
    return sonnerToast.loading(message, { ...options, id });
  },

  // Default toast
  default: (message, options = {}) => {
    const id = options.id || generateToastId('default');
    return sonnerToast(message, { ...options, id });
  },

  // Direct sonner methods for advanced usage
  dismiss: sonnerToast.dismiss,
  promise: sonnerToast.promise,
  
  // Custom method for when you need a specific clean ID
  withCleanId: (type, message, customId, options = {}) => {
    // Clean the custom ID by removing special characters
    const cleanId = customId.replace(/[^a-zA-Z0-9-_]/g, '_').substring(0, 50);
    const finalId = `${type}-${cleanId}`;
    
    return sonnerToast[type] ? 
      sonnerToast[type](message, { ...options, id: finalId }) :
      sonnerToast(message, { ...options, id: finalId });
  }
};

// Make the default export callable (for simple usage like: toast('message'))
const callableToast = (message, options = {}) => {
  return toast.default(message, options);
};

// Copy all methods to the callable function
Object.assign(callableToast, toast);

export default callableToast;